import type { SegmentedControlProps } from './SegmentedControl.types'
import './SegmentedControl.scss'

/**
 * Inline tab-style switcher used in Settings rows as a Select-replacement
 * for short option lists (2–3 items). Matches the Figma "Tabs" pattern:
 * muted track + elevated active pill with shadow.
 *
 * @example
 * <SegmentedControl
 *   value={mode}
 *   onValueChange={setMode}
 *   options={[
 *     { value: 'light', label: 'Hell', icon: <Sun /> },
 *     { value: 'dark', label: 'Dunkel', icon: <Moon /> },
 *     { value: 'system', label: 'System', icon: <Monitor /> },
 *   ]}
 * />
 */
export function SegmentedControl<T extends string>({
  value,
  onValueChange,
  options,
  id,
  className,
  ...rest
}: SegmentedControlProps<T>) {
  return (
    <div
      className={`segmented-control ${className ?? ''}`.trim()}
      role="tablist"
      aria-label={rest['aria-label']}
    >
      {options.map((option) => {
        const isSelected = option.value === value
        return (
          <button
            key={option.value}
            type="button"
            id={isSelected ? id : undefined}
            role="tab"
            aria-selected={isSelected}
            className={`segmented-control__option ${isSelected ? 'segmented-control__option--selected' : ''}`.trim()}
            onClick={() => {
              if (!isSelected) onValueChange(option.value)
            }}
          >
            {option.icon && <span className="segmented-control__icon">{option.icon}</span>}
            <span className="segmented-control__label">{option.label}</span>
          </button>
        )
      })}
    </div>
  )
}
